/**
 * Resolves the effective branch office ID for the current user session.
 *
 * Priority:
 *  1. A specific branch UUID selected in the branch selector (store).
 *  2. The user's own `branchId` from the auth store (used when the branch
 *     selector is hidden because the user only has access to one branch).
 *  3. `undefined` — when the user is in "all-branches" / business-wide context.
 *
 * `isBranchSelected` — true when an effective branch UUID is resolved;
 *                      use as `enabled` guard on page-level queries.
 * `isBranchReady`    — true when any branch value is set (incl. 'all');
 *                      use as `enabled` guard on layout-level queries.
 */
import { useAuthStore } from '../store/auth.store';
import { useBranchStore } from '../store/branch.store';
import {
  useCanSwitchBranch,
  useIsBranchUnrestricted,
} from './use-can-switch-branch';

export const useBranchOfficeId = () => {
  const selectedBranchOfficeId = useBranchStore(
    (state) => state.selectedBranchOfficeId
  );
  const branchOffices = useBranchStore((state) => state.branchOffices);
  const userBranchId = useAuthStore((state) => state.user?.branchId);

  // Only a branch-switcher (admin role OR `users.access_all_branches`) may
  // honor the store — see `useCanSwitchBranch`, the single decider shared
  // with the BranchSelector. For everyone else the branch is FIXED to their
  // JWT `user.branchId` — the persisted `selectedBranchOfficeId` must be
  // ignored. The store is localStorage-persisted and the selector (which
  // corrects a stale value) never mounts for a non-switcher, so a leftover
  // VALID sibling branch from a prior session/user on the same browser would
  // otherwise resolve here → the operator silently reads/prints ANOTHER
  // branch's data (invoice template / print config). Reported: non-admin
  // branch users printing with another branch's configuration.
  const canSwitchBranch = useCanSwitchBranch();

  // Explicit "All branches" pick — caller wants a business-wide view. Only
  // a branch-unrestricted role (admin / super_admin) may hold it: a
  // multi-branch user reaches just their granted branches, so a persisted
  // 'all' falls through to a single branch for them.
  const isBranchUnrestricted = useIsBranchUnrestricted();
  const userPickedAll =
    isBranchUnrestricted && selectedBranchOfficeId === 'all';

  // The branch store is persisted in localStorage, so a UUID from a
  // previous business / session can leak across logins. Validate the
  // stored id against the freshly-loaded branchOffices list for the
  // current business; if it doesn't belong, treat as no selection.
  // Without this guard, /settings/business and any other endpoint
  // guarded by ownership returns 403 "branchOfficeId does not belong
  // to this business".
  const storeHasSpecificBranch =
    !!selectedBranchOfficeId && selectedBranchOfficeId !== 'all';
  const storeBranchIsValid =
    storeHasSpecificBranch &&
    (branchOffices.length === 0 ||
      branchOffices.some((b) => b.id === selectedBranchOfficeId));
  const userBranchIsValid =
    !!userBranchId &&
    (branchOffices.length === 0 ||
      branchOffices.some((b) => b.id === userBranchId));

  // A non-switcher is pinned to their own branch: honor the store ONLY for
  // admins who can actually switch. (A non-admin with no assigned branch
  // falls through to undefined = business-wide, same as before.)
  const effectiveBranchOfficeId = !canSwitchBranch
    ? userBranchIsValid
      ? userBranchId
      : undefined
    : userPickedAll
    ? undefined
    : storeBranchIsValid
    ? selectedBranchOfficeId
    : userBranchIsValid
    ? userBranchId
    : undefined;

  return {
    branchOfficeId: effectiveBranchOfficeId,
    /** True when an effective branch UUID is resolved (store or user fallback). */
    isBranchSelected: !!effectiveBranchOfficeId,
    /** True when any branch value is set in the store, OR when the user has an
     *  assigned branch (non-admin fallback). */
    isBranchReady: !!selectedBranchOfficeId || !!effectiveBranchOfficeId,
  };
};

/** Branch contact fields consumed by print/receipt headers. */
export interface BranchPrintContact {
  /** Operating branch id — lets the config builder tell an own-branch
   *  template from an inherited HQ/global one when deciding contact
   *  precedence. */
  id?: string;
  name?: string;
  phone?: string;
  email?: string;
  website?: string;
  addressLine1?: string;
}

/**
 * The currently-selected branch's contact fields for print/receipt
 * headers, or `undefined` in the business-wide ("all") context. Feed into
 * `buildReceiptConfig(..., branch)` so a payment receipt prints the
 * operating branch's own contact block (branch-first) — the same
 * precedence the invoice print uses via `buildInvoiceConfig`.
 */
export const useSelectedBranchForPrint = (): BranchPrintContact | undefined => {
  const { branchOfficeId } = useBranchOfficeId();
  const branchOffices = useBranchStore((state) => state.branchOffices);
  if (!branchOfficeId) return undefined;
  const branch = Array.isArray(branchOffices)
    ? branchOffices.find((b) => b.id === branchOfficeId)
    : undefined;
  if (!branch) return undefined;
  return {
    id: branch.id,
    name: branch.name,
    phone: branch.phone ?? undefined,
    email: branch.email ?? undefined,
    website: branch.website ?? undefined,
    addressLine1: branch.addressLine1 ?? undefined,
  };
};
